import AttachmentOutlinedIcon from "@mui/icons-material/AttachmentOutlined";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import ImageIcon from "@mui/icons-material/Image";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useRef, useState } from "react";
import Flex from "@/components/Flex";
import { VisuallyHiddenInput } from "@/components/VisuallyHiddenInput";

type ChatAttachmentProps = {
  onAttach?: (file: File | null, fileExtension: "file" | "image") => void;
  disabled?: boolean;
};

const ChatAttachment = ({ onAttach, disabled }: ChatAttachmentProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "file" | "image"
  ) => {
    const file = e.target.files?.item?.(0) || null;
    if (file) {
      onAttach?.(file, type);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    // Close the attachment menu once a file has been picked.
    handleClose();
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const triggerImageInput = () => {
    imageInputRef.current?.click();
  };

  return (
    <>
      <Flex
        onClick={handleOpen}
        sx={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <AttachmentOutlinedIcon fontSize="small" />
      </Flex>

      <VisuallyHiddenInput
        ref={fileInputRef}
        onChange={(e) => handleFileInputChange(e, "file")}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
      />
      <VisuallyHiddenInput
        ref={imageInputRef}
        onChange={(e) => handleFileInputChange(e, "image")}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp"
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MenuItem onClick={triggerFileInput}>
          <ListItemIcon>
            <CreateNewFolderIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Attach File</ListItemText>
        </MenuItem>
        <MenuItem onClick={triggerImageInput}>
          <ListItemIcon>
            <ImageIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Attach Image</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ChatAttachment;
